import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Alert,
  Box,
  CircularProgress,
  Link,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  Timestamp,
  where,
} from 'firebase/firestore';
import { db } from '../../firebase';
import { useCompanyAiUsageMonth } from '../../hooks/useCompanyAiUsageMonth';
import { isAdminRole } from '../../lib/rolePermissions';
import { dashboardTicketPath } from '../../lib/dashboardRoutes';
import {
  currentMonthDateRangeUtc,
  formatUsd,
  groupAiUsageLogs,
  utcMonthKey,
} from '../../lib/aiUsageSpend';
import {
  normalizeAiSubscriptions,
  sumActiveSubscriptionUsd,
  sumCostEntriesUsd,
} from '../../lib/aiSubscriptions';

/**
 * Admin breakdown of current-month AI usage (day / model / ticket).
 * Requires composite index: aiUsageLog companyId ASC + createdAt DESC.
 * @param {{ userData?: { companyId?: string, role?: string } | null }} props
 */
export default function AiUsageSpendPanel({ userData }) {
  const companyId =
    typeof userData?.companyId === 'string' ? userData.companyId.trim() : '';
  const allowed = isAdminRole(userData?.role) && Boolean(companyId);

  const {
    monthKey,
    spendUsd,
    callCount,
    pricingMissingCount,
    budgetUsd,
    loading: summaryLoading,
    error: summaryError,
  } = useCompanyAiUsageMonth(userData);

  const [logs, setLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(allowed);
  const [logsError, setLogsError] = useState('');
  const [subscriptionUsd, setSubscriptionUsd] = useState(0);
  const [ledgerFixedUsd, setLedgerFixedUsd] = useState(0);

  useEffect(() => {
    if (!allowed) {
      setLogs([]);
      setLogsLoading(false);
      setLogsError('');
      return undefined;
    }

    setLogsLoading(true);
    setLogsError('');

    const { start, endExclusive } = currentMonthDateRangeUtc();

    const q = query(
      collection(db, 'aiUsageLog'),
      where('companyId', '==', companyId),
      where('createdAt', '>=', Timestamp.fromDate(start)),
      where('createdAt', '<', Timestamp.fromDate(endExclusive)),
      orderBy('createdAt', 'desc'),
    );

    const unsub = onSnapshot(
      q,
      (snap) => {
        setLogs(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLogsLoading(false);
      },
      (err) => {
        console.error('AiUsageSpendPanel logs:', err);
        setLogsError(
          err?.message ||
            'Failed to load AI usage log. If this mentions a missing index, deploy firestore indexes.',
        );
        setLogs([]);
        setLogsLoading(false);
      },
    );

    return () => unsub();
  }, [allowed, companyId, monthKey]);

  useEffect(() => {
    if (!allowed) {
      setSubscriptionUsd(0);
      setLedgerFixedUsd(0);
      return undefined;
    }

    const unsubCompany = onSnapshot(
      doc(db, 'companies', companyId),
      (snap) => {
        const settings = snap.exists() ? snap.data()?.settings || {} : {};
        setSubscriptionUsd(
          sumActiveSubscriptionUsd(
            normalizeAiSubscriptions(settings.aiSubscriptions),
          ),
        );
      },
      (err) => {
        console.error('AiUsageSpendPanel subscriptions:', err);
        setSubscriptionUsd(0);
      },
    );

    const unsubLedger = onSnapshot(
      collection(db, 'companies', companyId, 'costEntries'),
      (snap) => {
        const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        setLedgerFixedUsd(sumCostEntriesUsd(rows, monthKey || utcMonthKey()));
      },
      (err) => {
        console.error('AiUsageSpendPanel costEntries:', err);
        setLedgerFixedUsd(0);
      },
    );

    return () => {
      unsubCompany();
      unsubLedger();
    };
  }, [allowed, companyId, monthKey]);

  const grouped = useMemo(() => groupAiUsageLogs(logs), [logs]);
  // Prefer live ledger for the month when present; fall back to settings sum.
  const fixedUsd = ledgerFixedUsd > 0 ? ledgerFixedUsd : subscriptionUsd;
  const totalRunRateUsd = spendUsd + fixedUsd;

  if (!allowed) {
    return (
      <Alert severity="warning" sx={{ maxWidth: 560 }}>
        AI spend is available to Company Admins and Super Admins only.
      </Alert>
    );
  }

  const hasBudget = typeof budgetUsd === 'number' && budgetUsd > 0;
  const pct = hasBudget
    ? Math.min(100, Math.round((spendUsd / budgetUsd) * 100))
    : 0;
  const loading = summaryLoading || logsLoading;

  return (
    <Stack spacing={3} sx={{ maxWidth: 960 }}>
      <Box>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          AI spend
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Usage for {monthKey} (UTC). Internal cost estimates from logged token
          counts — not Anthropic remaining balance.
        </Typography>
      </Box>

      {(summaryError || logsError) && (
        <Alert severity="error">{summaryError || logsError}</Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack spacing={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Usage this month: {formatUsd(spendUsd)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              · {callCount} call{callCount === 1 ? '' : 's'}
            </Typography>
          </Typography>
          <Typography variant="body2">
            Subscriptions / fixed this month:{' '}
            <strong>{formatUsd(fixedUsd)}</strong>
          </Typography>
          <Typography variant="body2">
            Total run-rate (usage + fixed):{' '}
            <strong>{formatUsd(totalRunRateUsd)}</strong>
            <Typography
              component="span"
              variant="caption"
              color="text.secondary"
              sx={{ ml: 1 }}
            >
              informational only — alerts use usage vs budget
            </Typography>
          </Typography>
          {hasBudget ? (
            <>
              <Typography variant="body2" color="text.secondary">
                Usage budget: {formatUsd(spendUsd)} of {formatUsd(budgetUsd)}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={loading ? 0 : pct}
                color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
                sx={{ height: 8, borderRadius: 1 }}
              />
            </>
          ) : (
            <Typography variant="caption" color="text.secondary">
              No monthly usage budget set. Configure one under{' '}
              <Link
                component={RouterLink}
                to="/dashboard/workspace?section=ai-spend"
                underline="hover"
              >
                Settings → Workspace → AI Spend
              </Link>
              .
            </Typography>
          )}
          {pricingMissingCount > 0 ? (
            <Alert severity="warning" sx={{ py: 0 }}>
              {pricingMissingCount} call
              {pricingMissingCount === 1 ? '' : 's'} unpriced — total may be
              higher.
            </Alert>
          ) : null}
        </Stack>
      </Paper>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={28} />
        </Box>
      ) : (
        <>
          <BreakdownTable
            title="By day"
            columns={['Day (UTC)', 'Calls', 'Spend']}
            rows={grouped.byDay.map((r) => [
              r.day,
              String(r.callCount),
              formatUsd(r.spendUsd),
            ])}
          />
          <BreakdownTable
            title="By model"
            columns={['Model', 'Calls', 'Spend']}
            rows={grouped.byModel.map((r) => [
              r.model,
              String(r.callCount),
              formatUsd(r.spendUsd),
            ])}
          />
          <BreakdownTable
            title="By ticket"
            columns={['Ticket', 'Calls', 'Spend']}
            rows={grouped.byTicket.map((r) => [
              r.ticketId ? (
                <Link
                  key={r.ticketId}
                  component={RouterLink}
                  to={dashboardTicketPath(r.ticketId)}
                  underline="hover"
                >
                  {r.ticketId}
                </Link>
              ) : (
                '(none)'
              ),
              String(r.callCount),
              formatUsd(r.spendUsd),
            ])}
          />
        </>
      )}
    </Stack>
  );
}

/**
 * @param {{
 *   title: string,
 *   columns: string[],
 *   rows: Array<Array<React.ReactNode>>,
 * }} props
 */
function BreakdownTable({ title, columns, rows }) {
  return (
    <Paper variant="outlined">
      <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {title}
        </Typography>
      </Box>
      {rows.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          No usage logged this month yet.
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((c) => (
                  <TableCell key={c}>{c}</TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((cells, idx) => (
                <TableRow key={idx}>
                  {cells.map((cell, i) => (
                    <TableCell key={i}>{cell}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
